import { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import sameVoucherCode from 'components/cart/voucher-code'
import VoucherListModal from 'components/pdp/carousel-images/voucher/voucher-modal/VoucherListModal'
import { getVoucherCheckoutLists } from 'components/pdp/carousel-images/voucher/voucher-checkout/utils'
import VoucherApiHandler from 'lib/api/voucher'
import { customFlagValue } from 'lib/utils/handle-flag/const'

// Cart responses are JSON. Preserve every field that could affect eligibility,
// but ignore object identity and key order when deciding whether to fetch again.
const contextSignature = (context) =>
  JSON.stringify(context, (key, value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
      return value
    return Object.keys(value)
      .sort()
      .reduce((sorted, field) => ({ ...sorted, [field]: value[field] }), {})
  })

const VoucherCheckout = ({
  onClose,
  applyVoucher,
  cart,
  customerId,
  isGuestMode,
  shop,
  country,
  language,
  operation,
  allowManualCode,
}) => {
  const applied_voucher = cart?.summary?.applied_voucher
  const [isLoading, setIsLoading] = useState(true)
  const [allVouchersList, setAllVouchersList] = useState([])
  const [loadedContext, setLoadedContext] = useState(null)
  const confirmedVoucher =
    applied_voucher?.is_applied === true ? applied_voucher.code : ''
  const [pendingVoucher, setPendingVoucher] = useState('')
  const [applicationError, setApplicationError] = useState(null)
  const [listError, setListError] = useState(false)
  const [listAttempt, setListAttempt] = useState(0)
  const applying = useRef(null)
  const mounted = useRef(true)
  const dismissed = useRef(false)
  const observedOperation = useRef(null)
  const cartId =
    cart?.metadata?.id_cart == null ? null : String(cart.metadata.id_cart)
  const identity = contextSignature({
    cartId,
    customerId,
    isGuestMode,
    shop,
    country,
    language,
  })
  const currentIdentity = useRef(identity)
  currentIdentity.current = identity
  const isApplying = !!pendingVoucher || operation?.status === 'pending'
  const voucherCheckoutNewErrorFlagValue = customFlagValue({
    flagName: 'voucherCheckout',
  }) // TODO: force value to handle with flagsmith later (VoucherCheckoutNewError-20230101)

  const requestContext = contextSignature({
    cart,
    customerId,
    isGuestMode,
    shop,
    country,
    language,
    voucherCheckoutNewErrorFlagValue,
  })
  const isCurrentContextLoading = isLoading || loadedContext !== requestContext

  const handleClose = () => {
    dismissed.current = true
    onClose()
  }

  const handleApply = async (code, manual = false) => {
    if (
      !code?.trim() ||
      (!manual && (isCurrentContextLoading || listError)) ||
      applying.current ||
      isApplying ||
      sameVoucherCode(code, confirmedVoucher)
    )
      return
    const attempt = { code, identity }
    applying.current = attempt
    dismissed.current = false
    setPendingVoucher(code)
    setApplicationError(null)
    try {
      const result = await applyVoucher({
        type: 'USE_VOUCHER',
        voucherCode: code,
        apply_method: manual ? 'organic' : 'voucher_checkout',
      })
      if (
        !mounted.current ||
        dismissed.current ||
        currentIdentity.current !== attempt.identity
      )
        return
      const voucher = result?.cart?.summary?.applied_voucher
      const sameCart =
        cartId != null &&
        result?.cart?.metadata?.id_cart != null &&
        String(result.cart.metadata.id_cart) === cartId
      if (
        result?.status === 'confirmed' &&
        sameCart &&
        sameVoucherCode(voucher?.code, code) &&
        voucher?.is_applied === true
      ) {
        handleClose()
      } else {
        setApplicationError({
          status:
            result?.status === 'rejected' || result?.status === 'busy'
              ? result.status
              : 'unconfirmed',
          message: result?.message || result?.error?.message,
        })
      }
    } catch {
      if (
        mounted.current &&
        !dismissed.current &&
        currentIdentity.current === attempt.identity
      ) {
        setApplicationError({ status: 'unconfirmed' })
      }
    } finally {
      if (applying.current === attempt) {
        applying.current = null
        if (mounted.current) setPendingVoucher('')
      }
    }
  }

  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )

  useEffect(() => {
    if (operation?.kind === 'voucher' && operation.status === 'pending') {
      if (!observedOperation.current)
        observedOperation.current = { code: operation.code, identity }
    } else if (observedOperation.current) {
      const previous = observedOperation.current
      if (
        operation?.status === 'confirmed' &&
        previous.identity === identity &&
        sameVoucherCode(previous.code, confirmedVoucher) &&
        sameVoucherCode(operation.code, confirmedVoucher) &&
        !dismissed.current
      ) {
        dismissed.current = true
        onClose()
      }
      observedOperation.current = null
    }
  }, [operation, identity, confirmedVoucher, onClose])

  useEffect(() => {
    let active = true
    const context = JSON.parse(requestContext)
    setIsLoading(true)
    setAllVouchersList([])
    setListError(false)

    if (context.cart?.summary && Array.isArray(context.cart.items)) {
      // Keep the normal uncached endpoint; there is no cross-cart/user cache.
      VoucherApiHandler.getVoucher(true)
        .then((res) => {
          if (!active) return
          if (!Array.isArray(res?.vouchers))
            throw new Error('Invalid voucher list')
          getVoucherCheckoutLists(
            res.vouchers,
            context.cart.items,
            context.cart.summary.subtotal,
            setAllVouchersList,
            context.voucherCheckoutNewErrorFlagValue,
          )
          setLoadedContext(requestContext)
          setIsLoading(false)
        })
        .catch(() => {
          if (!active) return
          setAllVouchersList([])
          setListError(true)
          setLoadedContext(requestContext)
          setIsLoading(false)
        })
    }

    return () => {
      active = false
    }
  }, [requestContext, listAttempt])

  const operationError =
    operation?.kind === 'voucher' &&
    ['rejected', 'unconfirmed'].includes(operation.status)
      ? operation
      : null

  return (
    <VoucherListModal
      isLoading={isCurrentContextLoading}
      onClose={handleClose}
      voucherList={allVouchersList}
      onVoucherSelected={(code) => handleApply(code)}
      onCodeEntered={
        allowManualCode ? (code) => handleApply(code.trim(), true) : undefined
      }
      appliedVoucher={confirmedVoucher}
      isApplying={isApplying}
      pendingVoucher={
        pendingVoucher || (operation?.kind === 'voucher' ? operation.code : '')
      }
      applicationError={isApplying ? null : applicationError || operationError}
      listError={listError}
      onRetry={() => setListAttempt((attempt) => attempt + 1)}
      language={language}
      isCheckout
    />
  )
}

VoucherCheckout.defaultProps = {
  customerId: null,
  isGuestMode: true,
  shop: null,
  country: '',
  language: '',
  operation: null,
  allowManualCode: false,
}

VoucherCheckout.propTypes = {
  onClose: PropTypes.func.isRequired,
  applyVoucher: PropTypes.func.isRequired,
  allowManualCode: PropTypes.bool,
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isGuestMode: PropTypes.bool,
  shop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  country: PropTypes.string,
  language: PropTypes.string,
  operation: PropTypes.shape({
    kind: PropTypes.string,
    code: PropTypes.string,
    status: PropTypes.string,
    message: PropTypes.string,
  }),
  cart: PropTypes.shape({
    metadata: PropTypes.shape({
      id_cart: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    summary: PropTypes.shape({
      subtotal: PropTypes.number,
      applied_voucher: PropTypes.shape({
        code: PropTypes.string,
        is_applied: PropTypes.bool,
      }),
    }).isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id_product: PropTypes.number,
        quantity: PropTypes.number,
      }),
    ).isRequired,
  }).isRequired,
}

export default connect((state) => ({
  cart: state.cart.payload,
  customerId:
    state.auth?.user?.id_customer ?? state.auth?.customerData?.id_customer,
  isGuestMode: state.auth?.isGuestMode,
  shop: state.internationalization?.shop,
  country: state.internationalization?.country,
  language: state.internationalization?.language,
  operation: state.cart.operation,
}))(VoucherCheckout)
